import { Injectable } from '@angular/core';

import Logger from '@mcom/log4ts/build/Logger';
import BasicLayout from '@mcom/log4ts/build/layouts/BasicLayout';
import ConsoleAppender from '@mcom/log4ts/build/appenders/ConsoleAppender';
import LoggerConfig from '@mcom/log4ts/build/LoggerConfig';
import { LogLevel } from '@mcom/log4ts/build/LogLevel';
import { AnalyticsAppender } from '../logging/internal/analytics-appender';
import { environment } from '../../../environments/environment';

@Injectable()
export class LoggerService {
  public log: Logger;

  private readonly config: LoggerConfig;

  constructor() {
    const logLevel: number = environment.logLevel;

    const config = new LoggerConfig();
    config.setLevel(logLevel);

    const layout = new BasicLayout();
    const analyticsAppender = new AnalyticsAppender();
    const consoleAppender = new ConsoleAppender();
    consoleAppender.setLayout(layout);

    config.addAppender(analyticsAppender);
    config.addAppender(consoleAppender);

    Logger.setConfig(config);

    this.config = config;
  }

  getLogger(forClass?: string): ILogger {
    return new SerializingLogger(new Logger(forClass), this.config, forClass);
  }
}

export interface ILogger {
  info(message: string, ...params: any[]);
  fatal(message: string, ...params: any[]);
  error(message: string, ...params: any[]);
  debug(message: string, ...params: any[]);
  warn(message: string, ...params: any[]);
  trace(message: string, ...params: any[]);
}

/**
 * log4ts serializes object parameters with a bare JSON.stringify, which yields {} for an Error and
 * throws on the circular references Angular attaches to its errors. Handing it only primitives keeps
 * that branch unreachable.
 */
class SerializingLogger implements ILogger {
  constructor(private readonly inner: Logger, private readonly config: LoggerConfig, private readonly tag?: string) {}

  info(message: string, ...params: any[]) {
    this.inner.info(message, ...this.serialize(LogLevel.INFO, params));
  }

  fatal(message: string, ...params: any[]) {
    this.inner.fatal(message, ...this.serialize(LogLevel.FATAL, params));
  }

  error(message: string, ...params: any[]) {
    this.inner.error(message, ...this.serialize(LogLevel.ERROR, params));
  }

  debug(message: string, ...params: any[]) {
    this.inner.debug(message, ...this.serialize(LogLevel.DEBUG, params));
  }

  warn(message: string, ...params: any[]) {
    this.inner.warn(message, ...this.serialize(LogLevel.WARN, params));
  }

  trace(message: string, ...params: any[]) {
    this.inner.trace(message, ...this.serialize(LogLevel.TRACE, params));
  }

  // Same condition log4ts applies in doLog, so a dropped entry costs nothing.
  private serialize(level: LogLevel, params: any[]): any[] {
    if (level < this.config.getLevel() || !this.config.hasTag(this.tag)) {
      return params;
    }

    return params.map((param) => (typeof param === 'object' && param !== null ? SerializingLogger.toText(param) : param));
  }

  private static toText(value: any): string {
    const seen = new WeakSet();

    try {
      return JSON.stringify(value, (key, item) => {
        if (item instanceof Error) {
          return { name: item.name, message: item.message, stack: item.stack };
        }

        if (typeof item === 'object' && item !== null) {
          if (seen.has(item)) {
            return '[circular]';
          }

          seen.add(item);
        }

        return item;
      });
    } catch {
      return Object.prototype.toString.call(value);
    }
  }
}
